import { useEffect, useRef, useState } from "react";
import { api, payload } from "./lib/api";
import { uploadMedia } from "./lib/upload-media";
import type { EscapeExperience } from "./EscapeExperienceWizard";

type Config = { enabled: boolean; timezone: "Europe/Rome"; contactPhone: string; experiences: EscapeExperience[] };
type MediaKey = "image" | "heroImage" | "storyImage" | "presentationPoster" | "presentationVideo";

const MEDIA: Array<[MediaKey, string]> = [
  ["image", "Foto anteprima e sfondo della pagina (card della home e testata della pagina)"],
  ["heroImage", "Foto «Nel mondo reale»"],
  ["storyImage", "Foto «Dentro la storia»"],
];

// Pannello rapido dentro l'editor di pagina: testata della home e foto prima/dopo.
// Scrive negli stessi dati della scheda esperienza, quindi non può andare fuori sincronia.
export default function HeroQuickEditor({ siteId, canEdit, initialSlug, onSaved, onClose }: {
  siteId: string; canEdit: boolean; initialSlug?: string; onSaved: () => void; onClose: () => void;
}) {
  const [config, setConfig] = useState<Config | null>(null);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function load() {
    try {
      const day = new Date().toISOString().slice(0, 10);
      const result = await api<Config>(`/api/sites/${siteId}/escape-operations?date=${day}`);
      setConfig(result);
      setSelected((current) => result.experiences.find((item) => item.slug === initialSlug)?.id || current || result.experiences.find((item) => item.editorial.showInHero)?.id || result.experiences[0]?.id || "");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Non riesco a caricare le esperienze");
    }
  }
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => { void load(); }, [siteId, initialSlug]);
  useEffect(() => { root.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [initialSlug]);

  const current = config?.experiences.find((item) => item.id === selected);
  function change(value: Partial<EscapeExperience>) {
    setMessage("");
    setConfig((state) => state ? { ...state, experiences: state.experiences.map((item) => item.id === selected ? { ...item, ...value } : item) } : state);
  }
  function changeEditorial(value: Partial<EscapeExperience["editorial"]>) {
    if (current) change({ editorial: { ...current.editorial, ...value } });
  }

  async function upload(key: MediaKey, file: File) {
    if (!current) return;
    setBusy(key); setError("");
    try {
      const media = await uploadMedia(siteId, file, current.name);
      changeEditorial({ [key]: new URL(media.url, location.origin).href });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Caricamento non riuscito");
    } finally { setBusy(""); }
  }

  async function save() {
    if (!config || !canEdit) return;
    setBusy("save"); setError(""); setMessage("");
    try {
      await api(`/api/sites/${siteId}/escape-operations`, payload("PUT", {
        enabled: config.enabled,
        timezone: config.timezone,
        contactPhone: config.contactPhone,
        experiences: config.experiences,
      }));
      setMessage("Salvato: è già online.");
      await load();
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
    } finally { setBusy(""); }
  }

  const c = current?.editorial;
  const shown = (url: string) => url ? (url.startsWith("img/") ? `https://adventura-escape-room.pages.dev/${url}` : url) : "";
  return (
    <div className="hero-quick" ref={root} role="dialog" aria-label="Contenuti dell'esperienza">
      <div className="hero-quick-head">
        <strong>Contenuti dell'esperienza</strong>
        <button type="button" onClick={onClose} aria-label="Chiudi">×</button>
      </div>
      {!config && !error && <p>Caricamento…</p>}
      {config && current && c && (
        <>
          <label className="wiz-field">Esperienza
            <select value={selected} onChange={(event) => setSelected(event.target.value)}>
              {config.experiences.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label className="wiz-field">Titolo
            <input value={current.name} maxLength={120} disabled={!canEdit} onChange={(event) => change({ name: event.target.value })} />
          </label>
          <label className="wiz-field">Tipo di esperienza (riga piccola sopra il titolo)
            <input value={c.format} disabled={!canEdit} onChange={(event) => changeEditorial({ format: event.target.value })} placeholder="Escape room horror al castello" />
          </label>
          <label className="wiz-field">Frase introduttiva
            <input value={c.tagline} disabled={!canEdit} onChange={(event) => changeEditorial({ tagline: event.target.value })} />
          </label>
          <label className="wiz-field">Descrizione («Dentro la storia»)
            <textarea rows={5} maxLength={5000} value={c.description} disabled={!canEdit} onChange={(event) => changeEditorial({ description: event.target.value })} />
          </label>
          <label className="wiz-field">Frase sulla disponibilità (facoltativa)
            <input value={c.availabilityLabel} maxLength={160} disabled={!canEdit} onChange={(event) => changeEditorial({ availabilityLabel: event.target.value })} />
          </label>
          <label className="wiz-field">Luogo e indirizzo
            <input value={c.location} disabled={!canEdit} onChange={(event) => changeEditorial({ location: event.target.value })} />
          </label>
          <div className="hero-quick-numbers">
            <label className="wiz-field">Durata (minuti)
              <input type="number" min={15} max={480} value={current.durationMinutes} disabled={!canEdit} onChange={(event) => change({ durationMinutes: Number(event.target.value) })} />
            </label>
            <label className="wiz-field">Persone min
              <input type="number" min={1} value={current.minPartySize} disabled={!canEdit} onChange={(event) => change({ minPartySize: Number(event.target.value) })} />
            </label>
            <label className="wiz-field">Persone max
              <input type="number" min={1} value={current.maxPartySize} disabled={!canEdit} onChange={(event) => change({ maxPartySize: Number(event.target.value) })} />
            </label>
          </div>
          {(c.slotCapacity ?? 1) > 1 && <label className="wiz-field">Massimo persone per turno (0 = calcolo automatico)
            <input type="number" min={0} max={2000} value={c.maxPeoplePerSlot || 0} disabled={!canEdit} onChange={(event) => changeEditorial({ maxPeoplePerSlot: Math.max(0, Math.min(2000, Number(event.target.value) || 0)) })} />
          </label>}
          <label className="wiz-field">Prezzo (una voce per riga)
            <textarea rows={4} maxLength={800} value={c.price} disabled={!canEdit} onChange={(event) => changeEditorial({ price: event.target.value })} placeholder={"3 giocatori: 40 € a testa\n4-5 giocatori: 35 € a testa"} />
          </label>
          <label className="wiz-field">Stato sul sito
            <select value={c.visibility} disabled={!canEdit} onChange={(event) => changeEditorial({ visibility: event.target.value as "available" | "development" | "winter" | "hidden" })}>
              <option value="available">Online</option>
              <option value="development">In sviluppo</option>
              <option value="winter">In inverno</option>
              <option value="hidden">Nascosta (non compare sul sito)</option>
            </select>
          </label>
          <label className="wiz-field">Livello di difficoltà
            <select value={c.difficulty || ""} disabled={!canEdit} onChange={(event) => changeEditorial({ difficulty: event.target.value as "" | "facile" | "media" | "difficile" })}>
              <option value="">Non indicato</option>
              <option value="facile">Facile</option>
              <option value="media">Media</option>
              <option value="difficile">Difficile</option>
            </select>
          </label>
          <label className="wiz-field">Link Google Maps
            <input type="url" value={c.mapsUrl || ""} disabled={!canEdit} placeholder="https://maps.app.goo.gl/…" onChange={(event) => changeEditorial({ mapsUrl: event.target.value })} />
          </label>
          <label className="wiz-field">Etichetta sulla card
            <select value={c.highlight || "none"} disabled={!canEdit} onChange={(event) => changeEditorial({ highlight: event.target.value as "none" | "featured" | "exclusive" })}>
              <option value="none">Nessuna</option>
              <option value="featured">In evidenza</option>
              <option value="exclusive">Esclusivo</option>
            </select>
          </label>
          <div className="hero-quick-media">
            {MEDIA.map(([key, label]) => (
              <label key={key}>{label}
                {shown(c[key]) ? <img src={shown(c[key])} alt={label} /> : <small>Ora usa la foto predefinita del sito.</small>}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/avif" disabled={!canEdit}
                  onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(key, file); }} />
                <small>{busy === key ? "Caricamento…" : "JPG, PNG, WebP o AVIF, massimo 5 MB."}</small>
              </label>
            ))}
          </div>
          <label className="wiz-field">Video di presentazione (MP4 o WebM, max 20 MB)
            <input type="url" value={c.presentationVideo} disabled={!canEdit} placeholder="Indirizzo HTTPS del video" onChange={(event) => changeEditorial({ presentationVideo: event.target.value })} />
            <input type="file" accept="video/mp4,video/webm" disabled={!canEdit}
              onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload("presentationVideo", file); }} />
            <small>{busy === "presentationVideo" ? "Caricamento…" : c.presentationVideo ? "Compare come pulsante «Guarda il video». Per toglierlo svuota l'indirizzo." : "Compare come pulsante «Guarda il video», senza occupare spazio."}</small>
          </label>
        </>
      )}
      {error && <p className="hero-quick-error" role="alert">{error}</p>}
      {message && <p className="hero-quick-ok" role="status">{message}</p>}
      <button type="button" className="primary" disabled={!canEdit || !config || busy !== ""} onClick={() => void save()}>
        {busy === "save" ? "Salvo…" : "Salva e pubblica"}
      </button>
    </div>
  );
}
